import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  formatDeadlineDate,
  formatDeadlineUrgencyBadge,
} from '@/lib/dateUtils';
import { normTxt } from '@/lib/searchText';
import { EmptyRow, SearchField, SortHead, Th } from '@/ui';
import './SolicitudesTable.css';

function deadlineMs(item) {
  const ms = item.taskDeadline ? Date.parse(item.taskDeadline) : NaN;
  return Number.isNaN(ms) ? null : ms;
}

const blank = (value) => value == null || value === '' || value === '—';

// Requests with no deadline always sink, whatever the direction.
function compareBy(key, dir) {
  const sign = dir === 'desc' ? -1 : 1;
  return (a, b) => {
    if (key === 'plazo') {
      const x = deadlineMs(a);
      const y = deadlineMs(b);
      if (x == null && y == null) return 0;
      if (x == null) return 1;
      if (y == null) return -1;
      return (x - y) * sign;
    }
    // Rows with no value (the mapper puts "—" where a rol is missing) go last
    // in both directions, like the ones with no deadline.
    const x = blank(a[key]);
    const y = blank(b[key]);
    if (x || y) return x === y ? 0 : x ? 1 : -1;
    // numeric: roles like C-999-2024 sort by their numbers, not as text.
    return (
      String(a[key]).localeCompare(String(b[key]), 'es', { numeric: true }) *
      sign
    );
  };
}

export default function SolicitudesTable({
  items,
  answered = false,
  now = Date.now(),
  getItemHref,
  onOpen,
}) {
  const [query, setQuery] = useState('');
  // Until a header is clicked the rows keep the order the inbox hooks give
  // them (pendientes: nearest open deadline first, expired after; respondidas:
  // latest answer first), the same order the mobile list shows.
  const [sort, setSort] = useState(null);

  const rows = useMemo(() => {
    const needle = normTxt(query).trim();
    const shown = needle
      ? items.filter((item) =>
          normTxt(
            [item.rol, item.party, item.tipo, item.comuna].join(' '),
          ).includes(needle),
        )
      : items;
    return sort ? [...shown].sort(compareBy(sort.key, sort.dir)) : shown;
  }, [items, query, sort]);

  const head = (label, key, w) => (
    <SortHead
      label={label}
      k={key}
      sortKey={sort?.key}
      sortBy={sort?.dir}
      onSort={(k, dir) => setSort({ key: k, dir })}
      w={w}
    />
  );

  return (
    <div className="sol-table">
      <div className="sol-table__tools">
        <SearchField
          value={query}
          onChange={setQuery}
          placeholder="Buscar por código, estudio, actuación o comuna"
          kbd=""
        />
      </div>
      <div className="tbl-wrap sol-table__wrap">
        <table className="tbl sol-table__tbl">
          <caption className="sol-table__caption">
            {answered ? 'Solicitudes respondidas' : 'Solicitudes pendientes'}
          </caption>
          <thead>
            <tr>
              {head('Código', 'rol', 140)}
              {head('Estudio / demandado', 'party')}
              <Th w={200}>Actuación</Th>
              <Th w={140}>Comuna</Th>
              {head('Plazo', 'plazo', 210)}
              <Th w={130}>Estado</Th>
              <Th w={110} num>
                Monto
              </Th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <EmptyRow
                span={7}
                title="Sin resultados"
                hint="Prueba con otro código, estudio o comuna."
              />
            ) : (
              rows.map((item) => {
                const when = item.taskDeadline
                  ? formatDeadlineDate(item.taskDeadline)
                  : '';
                // An answered quote has nothing left to rush.
                const urgency =
                  !answered && item.taskDeadline
                    ? formatDeadlineUrgencyBadge(
                        item.taskDeadline,
                        now,
                        item.taskDeadlineExpiresAt,
                      )
                    : '';
                return (
                  <tr key={item.id} onClick={() => onOpen?.(item)}>
                    <td className="tbl__id">{item.rol}</td>
                    <td>
                      <Link
                        className="sol-table__party tbl__primary"
                        to={getItemHref?.(item) ?? `/solicitudes/${item.id}`}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {item.party}
                      </Link>
                    </td>
                    <td>
                      <div className="tbl__primary sol-table__plain">
                        {item.tipo}
                      </div>
                    </td>
                    <td>
                      <div className="tbl__primary sol-table__plain">
                        {item.comuna || '—'}
                      </div>
                    </td>
                    <td>
                      <span className="sol-table__due">
                        {when || '—'}
                        {urgency ? (
                          <span className="sol-table__pill">{urgency}</span>
                        ) : null}
                      </span>
                    </td>
                    <td>
                      <span className="sol-table__state">
                        {answered
                          ? (item.answerLabel ?? '—')
                          : (item.priceLabel ?? 'Por cotizar')}
                      </span>
                    </td>
                    <td className="num">{item.offeredPrice ?? '—'}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

SolicitudesTable.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string })),
  answered: PropTypes.bool,
  now: PropTypes.oneOfType([PropTypes.number, PropTypes.instanceOf(Date)]),
  getItemHref: PropTypes.func,
  onOpen: PropTypes.func,
};
